export type WorkOpeningNoticeType = 'shipping' | 'installation'

export const WORK_OPENING_NOTICE_TYPE_OPTIONS = [
  {
    value: 'shipping' as const,
    label: 'ขนส่งอย่างเดียว',
    title: 'ใบแจ้งเปิดงานขนส่งอย่างเดียว',
  },
  {
    value: 'installation' as const,
    label: 'ติดตั้งเสาเข็ม',
    title: 'ใบแจ้งเปิดงานติดตั้งเสาเข็ม',
  },
]

export type PileCode = '01' | '02' | '03' | '04' | '05'

export type PaymentStatus =
  | 'completed'
  | 'deposit'
  | 'promissory'
  | 'not_completed'
  | 'cheque'
  | ''

export type DeliverySiteOption = 'factory' | 'arrival' | 'completed' | 'not_completed' | ''

export interface LineItem {
  id: string
  no: string
  description: string
  typeModel: string
  quantity: string
}

export interface DeliverySiteState {
  option: DeliverySiteOption
  arrivalDate: Date | null
}

export interface PaymentState {
  status: PaymentStatus
  depositPercent: string
  promissoryDays: string
  notCompletedReason: string
}

export interface HeaderDocumentState {
  documentNo: string
  mapNo: string
  department: string
  documentDate: Date | null
  customerTitle: string
  division: string
  productionOrderNo: string
}

export type RecipientSlot = 'vns' | 'sk' | 'accounting'

export interface HeaderUserSelection {
  slot: RecipientSlot
  userId: number | null
}

export const RECIPIENT_SLOT_CONFIG: {
  slot: RecipientSlot
  label: string
  roleIds: number[]
}[] = [
  { slot: 'vns', label: 'เรียน — VNS', roleIds: [4, 15, 21, 19] },
  { slot: 'sk', label: 'เรียน — ฝ่าย SK', roleIds: [3, 16] },
  { slot: 'accounting', label: 'เรียน — ฝ่ายบัญชี', roleIds: [5, 7] },
]

export const PILE_CODES: PileCode[] = ['01', '02', '03', '04', '05']

export const createEmptyLineItem = (): LineItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  no: '',
  description: '',
  typeModel: '',
  quantity: '',
})

export const createInitialLineItems = (count = 5): LineItem[] =>
  Array.from({ length: count }, () => createEmptyLineItem())

// --- Installation form types ---

export type InstallationPileCode = '01' | '02' | '03' | '04' | '05' | '06'

export type InstallationRecipientSlot = 'vns' | 'kl' | 'sk' | 'vi' | 'accounting'

export interface InstallationHeaderUserSelection {
  slot: InstallationRecipientSlot
  userId: number | null
}

export const INSTALLATION_RECIPIENT_SLOT_CONFIG: {
  slot: InstallationRecipientSlot
  label: string
  roleIds: number[]
}[] = [
  { slot: 'vns', label: 'เรียน — VNS', roleIds: [4, 15, 21, 19] },
  { slot: 'kl', label: 'เรียน — KL', roleIds: [] },
  { slot: 'sk', label: 'เรียน — SK', roleIds: [3, 16] },
  { slot: 'vi', label: 'เรียน — VI', roleIds: [] },
  { slot: 'accounting', label: 'เรียน — บัญชี', roleIds: [5, 7] },
]

export const INSTALLATION_PILE_CODES: InstallationPileCode[] = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
]

export interface InstallationLineItem {
  id: string
  no: string
  description: string
  typeModel: string
  quantity: string
  receive: boolean
  hire: boolean
}

export type InstallationPilingSiteOption =
  | 'factory'
  | 'maturity'
  | 'completed'
  | 'not_completed'
  | ''

export interface InstallationPilingSiteState {
  option: InstallationPilingSiteOption
  maturityDate: Date | null
}

export interface InstallationCraneAppointment {
  enabled: boolean
  datetime: Date | null
}

export interface InstallationCraneScheduleState {
  mainPile: InstallationCraneAppointment
  auspiciousPile: InstallationCraneAppointment
  actualPile: InstallationCraneAppointment
  finishDate: Date | null
  totalWorkDays: string
  penaltyPerDay: string
}

export type InstallationCustomerDutyKey =
  | 'accessRoad'
  | 'forklift'
  | 'electricity'
  | 'weldingElectricity'
  | 'water'
  | 'accommodation'
  | 'staking'
  | 'toilet'
  | 'treeCutting'
  | 'siteClearing'
  | 'groutElectricity'
  | 'threePhaseElectricity'
  | 'groutWater'
  | 'other'

export const INSTALLATION_CUSTOMER_DUTY_OPTIONS: {
  key: InstallationCustomerDutyKey
  label: string
}[] = [
  { key: 'accessRoad', label: 'ทางเข้าออก' },
  { key: 'forklift', label: 'รถโฟล์คลิฟท์/เครน' },
  { key: 'electricity', label: 'ไฟฟ้า' },
  { key: 'weldingElectricity', label: 'ไฟเชื่อม' },
  { key: 'water', label: 'น้ำ' },
  { key: 'accommodation', label: 'ที่พัก' },
  { key: 'staking', label: 'หมุด/ปักหมุด' },
  { key: 'toilet', label: 'ห้องน้ำ' },
  { key: 'treeCutting', label: 'ตัดต้นไม้' },
  { key: 'siteClearing', label: 'ปรับพื้นที่' },
  { key: 'groutElectricity', label: 'ไฟยาแนว' },
  { key: 'threePhaseElectricity', label: 'ไฟ 3 เฟส' },
  { key: 'groutWater', label: 'น้ำยาแนว' },
  { key: 'other', label: 'อื่นๆ' },
]

export type InstallationCustomerDutyState = Record<InstallationCustomerDutyKey, boolean> & {
  otherText: string
}

export type InstallationContractStatus = 'signed' | 'in_progress' | ''

export interface InstallationContractState {
  status: InstallationContractStatus
}

export type InstallationFinanceStatus =
  | 'completed'
  | 'deposit'
  | 'pileQueue'
  | 'drivingTicket'
  | 'not_completed'
  | 'cheque'
  | ''

export interface InstallationFinanceState {
  status: InstallationFinanceStatus
  depositPercent: string
  pileQueueDays: string
  drivingTicketDays: string
  notCompletedReason: string
  chequeDetail: string
}

export interface InstallationSignatureBlock {
  printedName: string
  date: Date | null
}

export interface InstallationSignaturesState {
  salesRep: InstallationSignatureBlock
  salesDirector: InstallationSignatureBlock
  workOrderIssuer: InstallationSignatureBlock
  directorAcknowledged: InstallationSignatureBlock
}

export interface InstallationRigFooterState {
  rigOwner: string
  quantity: string
  hammerWeightTons: string
  rigOrganizer: string
  rigHeightMeters: string
  rigArrivalDate: Date | null
  date: Date | null
}

export const createEmptyInstallationLineItem = (): InstallationLineItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  no: '',
  description: '',
  typeModel: '',
  quantity: '',
  receive: false,
  hire: false,
})

export const createInitialInstallationLineItems = (count = 5): InstallationLineItem[] =>
  Array.from({ length: count }, () => createEmptyInstallationLineItem())

export const createInitialInstallationCustomerDuty = (): InstallationCustomerDutyState => ({
  accessRoad: false,
  forklift: false,
  electricity: false,
  weldingElectricity: false,
  water: false,
  accommodation: false,
  staking: false,
  toilet: false,
  treeCutting: false,
  siteClearing: false,
  groutElectricity: false,
  threePhaseElectricity: false,
  groutWater: false,
  other: false,
  otherText: '',
})

export const createInitialInstallationCraneAppointment = (): InstallationCraneAppointment => ({
  enabled: false,
  datetime: null,
})

export const INITIAL_INSTALLATION_PILING_SITE: InstallationPilingSiteState = {
  option: '',
  maturityDate: null,
}

export const INITIAL_INSTALLATION_CRANE_SCHEDULE: InstallationCraneScheduleState = {
  mainPile: createInitialInstallationCraneAppointment(),
  auspiciousPile: createInitialInstallationCraneAppointment(),
  actualPile: createInitialInstallationCraneAppointment(),
  finishDate: null,
  totalWorkDays: '',
  penaltyPerDay: '',
}

export const INITIAL_INSTALLATION_CONTRACT: InstallationContractState = {
  status: '',
}

export const INITIAL_INSTALLATION_FINANCE: InstallationFinanceState = {
  status: '',
  depositPercent: '',
  pileQueueDays: '',
  drivingTicketDays: '',
  notCompletedReason: '',
  chequeDetail: '',
}

export const INITIAL_INSTALLATION_SIGNATURE_BLOCK: InstallationSignatureBlock = {
  printedName: '',
  date: null,
}

export const INITIAL_INSTALLATION_SIGNATURES: InstallationSignaturesState = {
  salesRep: { ...INITIAL_INSTALLATION_SIGNATURE_BLOCK },
  salesDirector: { ...INITIAL_INSTALLATION_SIGNATURE_BLOCK },
  workOrderIssuer: { ...INITIAL_INSTALLATION_SIGNATURE_BLOCK },
  directorAcknowledged: { ...INITIAL_INSTALLATION_SIGNATURE_BLOCK },
}

export const INITIAL_INSTALLATION_RIG_FOOTER: InstallationRigFooterState = {
  rigOwner: '',
  quantity: '',
  hammerWeightTons: '',
  rigOrganizer: '',
  rigHeightMeters: '',
  rigArrivalDate: null,
  date: null,
}
